import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { COLORS, LAYOUT } from '../../constants';

// Badge Icons
const NewbieBadge = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#e8394b" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const SellerBadge = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d61c2e" strokeWidth="2">
    <path d="M20 7h-9" />
    <path d="M14 17H5" />
    <circle cx="17" cy="17" r="3" />
    <circle cx="7" cy="7" r="3" />
  </svg>
);

const BuyerBadge = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9d0d1d" strokeWidth="2">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const ActiveBadge = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#b01121" strokeWidth="2">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const VerifiedBadge = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.primary} strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const TrustedBadge = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8a0a18" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ProfileBadges = ({ userData }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calculateBadges = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const earnedBadges = [];

        // Check for email verification
        if (auth.currentUser?.emailVerified) {
          earnedBadges.push({
            id: 'verified',
            name: 'Verified',
            description: 'Email verified',
            icon: <VerifiedBadge />,
            color: COLORS.primary
          });
        }

        // Count listings for seller badges
        const listingsQuery = query(
          collection(db, 'listings'),
          where('sellerId', '==', userId)
        );
        const listingsSnapshot = await getDocs(listingsQuery);
        const listingCount = listingsSnapshot.size;

        if (listingCount >= 1) {
          earnedBadges.push({
            id: 'seller',
            name: 'Seller',
            description: `${listingCount} listing${listingCount > 1 ? 's' : ''}`,
            icon: <SellerBadge />,
            color: '#d61c2e' // Light ISU red
          });
        }

        if (listingCount >= 5) {
          earnedBadges.push({
            id: 'active-seller',
            name: 'Active Seller',
            description: '5+ listings',
            icon: <ActiveBadge />,
            color: '#b01121' // Medium ISU red
          });
        }

        // Count purchases for buyer badges
        const purchasesQuery = query(
          collection(db, 'transactions'),
          where('buyerId', '==', userId)
        );
        const purchasesSnapshot = await getDocs(purchasesQuery);
        const purchaseCount = purchasesSnapshot.size;

        if (purchaseCount >= 1) {
          earnedBadges.push({
            id: 'buyer',
            name: 'Buyer',
            description: `${purchaseCount} purchase${purchaseCount > 1 ? 's' : ''}`,
            icon: <BuyerBadge />,
            color: '#9d0d1d' // Dark ISU red
          });
        }

        // Count sales for trusted badge
        const salesQuery = query(
          collection(db, 'transactions'),
          where('sellerId', '==', userId)
        );
        const salesSnapshot = await getDocs(salesQuery);
        const salesCount = salesSnapshot.size;

        if (salesCount >= 3) {
          earnedBadges.push({
            id: 'trusted',
            name: 'Trusted Seller',
            description: '3+ completed sales',
            icon: <TrustedBadge />,
            color: '#8a0a18' // Very dark ISU red
          });
        }

        // Check member duration for newbie badge
        const joinedAt = userData.joinedAt?.toDate?.() || new Date();
        const daysSinceJoined = Math.floor((new Date() - joinedAt) / (1000 * 60 * 60 * 24));

        if (daysSinceJoined <= 30) {
          earnedBadges.push({
            id: 'newbie',
            name: 'New Member',
            description: 'Joined recently',
            icon: <NewbieBadge />,
            color: '#e8394b' // Very light ISU red
          });
        }

        setBadges(earnedBadges);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    calculateBadges();
  }, [userData]);

  const badgeStyles = {
    container: {
      background: '#fff',
      borderRadius: 16,
      padding: LAYOUT.spacing.xl,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
    },
    
    header: {
      fontSize: 20,
      fontWeight: 700,
      color: COLORS.primary,
      marginBottom: LAYOUT.spacing.lg
    },
    
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: LAYOUT.spacing.md
    },
    
    badge: {
      padding: LAYOUT.spacing.md,
      borderRadius: 12,
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: LAYOUT.spacing.xs,
      textAlign: 'center',
      transition: 'all 0.3s ease'
    },
    
    iconWrapper: (color) => ({
      width: 56,
      height: 56,
      borderRadius: 12,
      background: `${color}15`,
      color: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: LAYOUT.spacing.xs
    }),
    
    badgeName: {
      fontSize: 14,
      fontWeight: 700,
      color: COLORS.text.primary,
      margin: 0
    },
    
    badgeDescription: {
      fontSize: 11,
      color: COLORS.text.secondary,
      margin: 0
    },
    
    emptyState: {
      textAlign: 'center',
      padding: `${LAYOUT.spacing.xl}px 0`,
      color: COLORS.text.muted,
      fontSize: 14
    },
    
    loadingText: {
      textAlign: 'center',
      color: COLORS.text.secondary,
      padding: LAYOUT.spacing.lg
    }
  };

  if (loading) {
    return (
      <div style={badgeStyles.container}>
        <div style={badgeStyles.header}>Achievements</div>
        <div style={badgeStyles.loadingText}>Loading badges...</div>
      </div>
    );
  }

  return (
    <div style={badgeStyles.container}>
      <div style={badgeStyles.header}>
        Achievements {badges.length > 0 && `(${badges.length})`}
      </div>
      {badges.length === 0 ? (
        <div style={badgeStyles.emptyState}>
          Complete actions to earn badges
        </div>
      ) : (
        <div style={badgeStyles.grid}>
          {badges.map((badge) => (
            <div
              key={badge.id}
              style={badgeStyles.badge}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={badgeStyles.iconWrapper(badge.color)}>
                {badge.icon}
              </div>
              <div style={badgeStyles.badgeName}>{badge.name}</div>
              <div style={badgeStyles.badgeDescription}>{badge.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileBadges;
